import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import MainSaveButton from "../ui/components/MainSaveButton";
import { FaSearch, FaLock, FaTrashAlt } from "react-icons/fa";
import { DEFAULT_MAIL_ADMIN_WORKER_ID } from "../../constants/values";

const getAreaValue = (t) => {
  const a = t.area;
  if (!a) return "";
  return typeof a === "string" ? a.trim() : (a.nombre || a.name || "").trim();
};

const getSubareaValue = (t) => {
  const s = t.subarea ?? t.subArea;
  if (!s) return "";
  return typeof s === "string" ? s.trim() : (s.nombre || s.name || "").trim();
};

const AdminAccessModal = ({ open, onClose, account, trabajadores = [], onSave }) => {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const displayableWorkers = useMemo(() => {
    if (showInactive) return trabajadores;
    return trabajadores.filter((t) => t.activo !== false);
  }, [trabajadores, showInactive]);

  const areas = useMemo(() => {
    const set = new Set();
    displayableWorkers.forEach((t) => {
      const v = getAreaValue(t);
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [displayableWorkers]);

  const subareas = useMemo(() => {
    const set = new Set();
    displayableWorkers.forEach((t) => {
      const v = getSubareaValue(t);
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [displayableWorkers]);

  useEffect(() => {
    let ids = [];
    if (account?.trabajadoresConAcceso) {
      ids = account.trabajadoresConAcceso.map((t) =>
        typeof t === "string" ? t : t.id
      );
    }
    if (!ids.includes(DEFAULT_MAIL_ADMIN_WORKER_ID)) {
      ids = [DEFAULT_MAIL_ADMIN_WORKER_ID, ...ids];
    }
    setSelected(ids);
    setSearch("");
  }, [account, open]);

  const toggleWorker = (id) => {
    if (id === DEFAULT_MAIL_ADMIN_WORKER_ID) return; // No se puede desactivar
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearAll = () => {
    setSelected([DEFAULT_MAIL_ADMIN_WORKER_ID]);
  };

  const getFirstName = (t) => {
    const full = (t.nombre || t.name || "").trim();
    return full ? full.split(/\s+/)[0] : (t.email || "Sin nombre").split("@")[0];
  };

  const selectByArea = (area) => {
    const ids = displayableWorkers
      .filter((t) => getAreaValue(t) === area)
      .map((t) => t.id);
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const selectBySubarea = (subarea) => {
    const ids = displayableWorkers
      .filter((t) => getSubareaValue(t) === subarea)
      .map((t) => t.id);
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = [...new Set([DEFAULT_MAIL_ADMIN_WORKER_ID, ...selected])];
      await onSave(account.id, toSave);
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const filtered = displayableWorkers.filter((t) => {
    const q = search.toLowerCase();
    const fullName = `${t.nombre || ""} ${t.apellido || ""}`.toLowerCase();
    const areaStr = getAreaValue(t).toLowerCase();
    const subareaStr = getSubareaValue(t).toLowerCase();
    return (
      fullName.includes(q) ||
      (t.email || "").toLowerCase().includes(q) ||
      areaStr.includes(q) ||
      subareaStr.includes(q)
    );
  });

  if (!account) return null;

  return (
    <Modal
      activeModal={open}
      onClose={onClose}
      title={`Accesos: ${account.nombre || account.imapUser}`}
      className="max-w-8xl"
      themeClass="bg-slate-100"
      centered
      scrollContent
    >
      <div className="flex flex-col gap-4">
        <div className="text-xs text-gray-500">
          Cuenta: <span className="font-semibold text-gray-700">{account.imapUser}</span>
        </div>

        {/* Áreas */}
        <div>
          <h3 className="text-[11px] font-bold text-gray-700 mb-2">Áreas</h3>
          <div className="flex flex-wrap gap-2">
            {areas.length === 0 ? (
              <span className="text-[10px] text-gray-400">Sin áreas definidas</span>
            ) : (
              areas.map((area) => (
                <button
                  key={area}
                  type="button"
                  onClick={() => selectByArea(area)}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
                >
                  {area}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Sub-áreas */}
        <div>
          <h3 className="text-[11px] font-bold text-gray-700 mb-2">Sub-áreas</h3>
          <div className="flex flex-wrap gap-2">
            {subareas.length === 0 ? (
              <span className="text-[10px] text-gray-400">Sin sub-áreas definidas</span>
            ) : (
              subareas.map((subarea) => (
                <button
                  key={subarea}
                  type="button"
                  onClick={() => selectBySubarea(subarea)}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 hover:border-slate-300 transition-colors"
                >
                  {subarea}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Buscador y filtro inactivos */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* <div className="relative flex-1">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
            <input
              type="text"
              placeholder="Buscar trabajador por nombre, email, área..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-3 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-slate-400 bg-gray-50"
            />
          </div> */}
          <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-3.5 h-3.5 accent-slate-500 rounded"
            />
            <span className="text-[11px] font-medium text-gray-600">Mostrar trabajadores inactivos</span>
          </label>
        </div>

        {/* Trabajadores como burbujas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-gray-400">
              {selected.length} trabajador(es) con acceso — clic para seleccionar/deseleccionar
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            >
              <FaTrashAlt className="text-[9px]" />
              Limpiar selección
            </button>
          </div>
          <div className="flex flex-wrap gap-3 max-h-[320px] overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50/50 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {filtered.length === 0 ? (
              <div className="w-full py-8 text-center text-xs text-gray-400">
                No se encontraron trabajadores
              </div>
            ) : (
              filtered.map((t) => {
                const isSelected = selected.includes(t.id);
                const isLocked = t.id === DEFAULT_MAIL_ADMIN_WORKER_ID;
                const isInactive = t.activo === false;
                const firstName = getFirstName(t);
                const photoUrl = t.foto?.url;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleWorker(t.id)}
                    disabled={isLocked && isSelected}
                    className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all ${
                      isSelected
                        ? "bg-slate-600 text-white shadow-sm"
                        : "bg-white text-gray-600 border border-gray-200 hover:border-slate-300 hover:bg-slate-50"
                    } ${isLocked && isSelected ? "cursor-default" : ""} ${isInactive ? "opacity-80" : ""}`}
                    title={isLocked && isSelected ? "Siempre tiene acceso (no se puede desactivar)" : t.email}
                  >
                    <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 bg-slate-200 flex items-center justify-center">
                      {photoUrl ? (
                        <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className={`text-[10px] font-bold ${isSelected ? "text-slate-300" : "text-slate-500"}`}>
                          {firstName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[11px] font-semibold truncate">{firstName}</span>
                      <span className={`text-[10px] whitespace-nowrap ${isSelected ? "text-slate-200" : "text-gray-500"}`} title={t.email}>
                        {t.email}
                      </span>
                      {isLocked && isSelected && (
                        <span className="flex items-center gap-1 mt-0.5 text-[9px] opacity-80">
                          <FaLock className="text-[8px]" /> Siempre activo
                        </span>
                      )}
                      {isInactive && (
                        <span className={`mt-0.5 text-[9px] ${isSelected ? "text-slate-300" : "text-amber-600"}`}>
                          Inactivo
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-gray-100">
          <MainSaveButton
            text="Guardar accesos"
            onClick={handleSave}
            loading={saving}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AdminAccessModal;
